<template>
    <div class="st">
        <UserTopNav :active="'projects'" />

        <div class="st-container">
            <!-- ═══ Back link ═══ -->
            <router-link
                :to="{
                    name: 'ProjectDetails',
                    params: {
                        projectId: currentProjectId,
                        projectSlug: currentProjectSlug,
                    },
                }"
                class="st-back"
            >
                <font-awesome-icon icon="fa-solid fa-arrow-left" />
                <span>Inapoi la Proiect</span>
            </router-link>

            <!-- ═══ Header ═══ -->
            <div class="st-header">
                <div class="st-header__info">
                    <h1 class="st-header__name">{{ currentProjectName || '...' }}</h1>
                    <p class="st-header__sub">Statistici &amp; Analytics</p>
                    <div class="st-header__meta" v-if="scopedProjectData">
                        <span class="st-tag">
                            <font-awesome-icon icon="fa-solid fa-calendar-days" />
                            {{ formatDate(scopedProjectData.project_date) }}
                        </span>
                        <span class="st-tag st-tag--type">
                            {{ templateTypeLabel }}
                        </span>
                        <span v-if="scopedProjectData.event_type_label" class="st-tag st-tag--event">
                            {{ scopedProjectData.event_type_label }}
                        </span>
                        <span v-if="!isSimpleTemplate" class="st-tag st-tag--engagement">
                            <font-awesome-icon icon="fa-solid fa-bolt" />
                            Engagement {{ engagementScore }}%
                        </span>
                    </div>
                </div>
                <div class="st-header__actions" v-if="scopedProjectData">
                    <router-link
                        class="st-btn"
                        :to="{
                            name: 'ProjectDetailsInvitations',
                            params: {
                                projectId: currentProjectId,
                                projectSlug: currentProjectSlug,
                            },
                        }"
                    >
                        <font-awesome-icon icon="fa-solid fa-users-gear" />
                        <span>Gestioneaza invitatii</span>
                    </router-link>
                    <router-link
                        class="st-btn st-btn--ghost"
                        :to="{
                            name: 'ProjectDetailsConfig',
                            params: {
                                projectId: currentProjectId,
                                projectSlug: currentProjectSlug,
                            },
                        }"
                    >
                        <font-awesome-icon icon="fa-solid fa-sliders" />
                        <span>Setari proiect</span>
                    </router-link>
                </div>
            </div>

            <template v-if="stats && scopedProjectData">
                <!-- ═══ KPI grid ═══ -->
                <div class="st-kpi-grid">
                    <div class="st-kpi">
                        <div class="st-kpi__icon st-kpi__icon--blue">
                            <font-awesome-icon icon="fa-solid fa-users" />
                        </div>
                        <div class="st-kpi__body">
                            <span class="st-kpi__label">{{ isSimpleTemplate ? 'Raspunsuri' : 'Invitati' }}</span>
                            <span class="st-kpi__value">{{ totalInviteCount }}</span>
                        </div>
                    </div>

                    <div class="st-kpi" v-if="!isSimpleTemplate">
                        <div class="st-kpi__icon st-kpi__icon--violet">
                            <font-awesome-icon icon="fa-solid fa-envelope-circle-check" />
                        </div>
                        <div class="st-kpi__body">
                            <span class="st-kpi__label">Raspunsuri</span>
                            <span class="st-kpi__value">{{ answeredCount }}</span>
                            <span class="st-kpi__hint">{{ responseRate }}% rata de raspuns</span>
                        </div>
                    </div>

                    <div class="st-kpi">
                        <div class="st-kpi__icon st-kpi__icon--green">
                            <font-awesome-icon icon="fa-solid fa-user-check" />
                        </div>
                        <div class="st-kpi__body">
                            <span class="st-kpi__label">Confirmari</span>
                            <span class="st-kpi__value">{{ confirmationsCount }}</span>
                            <span class="st-kpi__hint">{{ confirmationRate }}% din total</span>
                        </div>
                    </div>

                    <div class="st-kpi">
                        <div class="st-kpi__icon st-kpi__icon--red">
                            <font-awesome-icon icon="fa-solid fa-user-slash" />
                        </div>
                        <div class="st-kpi__body">
                            <span class="st-kpi__label">Refuzuri</span>
                            <span class="st-kpi__value">{{ refusalsCount }}</span>
                            <span class="st-kpi__hint">{{ refusalRate }}% din total</span>
                        </div>
                    </div>

                    <div class="st-kpi" v-if="!isSimpleTemplate">
                        <div class="st-kpi__icon st-kpi__icon--amber">
                            <font-awesome-icon icon="fa-solid fa-clock" />
                        </div>
                        <div class="st-kpi__body">
                            <span class="st-kpi__label">Fara raspuns</span>
                            <span class="st-kpi__value">{{ pendingResponsesCount }}</span>
                            <span class="st-kpi__hint">Necesita follow-up</span>
                        </div>
                    </div>

                    <div class="st-kpi">
                        <div class="st-kpi__icon st-kpi__icon--cyan">
                            <font-awesome-icon icon="fa-solid fa-eye" />
                        </div>
                        <div class="st-kpi__body">
                            <span class="st-kpi__label">Vizualizari</span>
                            <span class="st-kpi__value">{{ totalViewsCount }}</span>
                            <span class="st-kpi__hint">{{ viewsPerInviteLabel }}</span>
                        </div>
                    </div>
                </div>

                <!-- ═══ Response rate bar ═══ -->
                <section class="st-card" v-if="totalInviteCount > 0">
                    <div class="st-card__header">
                        <div class="st-card__icon st-card__icon--blue">
                            <font-awesome-icon icon="fa-solid fa-chart-column" />
                        </div>
                        <div>
                            <h2 class="st-card__title">Rata de raspuns</h2>
                            <p class="st-card__desc">{{ responseRateLabel }}</p>
                        </div>
                    </div>
                    <div class="st-card__body">
                        <div class="st-progress">
                            <div class="st-progress__track">
                                <div class="st-progress__fill st-progress__fill--ok" :style="{ width: confirmationRate + '%' }"></div>
                                <div class="st-progress__fill st-progress__fill--no" :style="{ width: refusalRate + '%' }"></div>
                            </div>
                        </div>
                        <div class="st-split-row">
                            <span class="st-split st-split--ok">Participa: {{ confirmationRate }}%</span>
                            <span class="st-split st-split--no">Nu participa: {{ refusalRate }}%</span>
                            <span v-if="!isSimpleTemplate" class="st-split st-split--pending">Fara raspuns: {{ pendingRate }}%</span>
                        </div>
                    </div>
                </section>

                <!-- ═══ Insights row ═══ -->
                <div class="st-insights-row">
                    <!-- Funnel (complex only, or feature-flagged) -->
                    <section class="st-card" v-if="!isSimpleTemplate">
                        <div class="st-card__header">
                            <div class="st-card__icon st-card__icon--violet">
                                <font-awesome-icon icon="fa-solid fa-filter" />
                            </div>
                            <div>
                                <h2 class="st-card__title">Funnel invitatii</h2>
                                <p class="st-card__desc">{{ deliverySummary }}</p>
                            </div>
                        </div>
                        <div class="st-card__body">
                            <template v-if="hasFeature('stats_funnel') || !scopedProjectData.features?.length">
                                <div class="st-funnel" v-for="step in funnelSteps" :key="step.key">
                                    <div class="st-funnel__top">
                                        <span>{{ step.label }}</span>
                                        <strong>{{ step.value }}</strong>
                                    </div>
                                    <div class="st-bar">
                                        <div class="st-bar__fill st-bar__fill--blue" :style="{ width: step.rate + '%' }"></div>
                                    </div>
                                    <small class="st-funnel__pct">{{ step.rate }}%</small>
                                </div>
                            </template>
                            <p v-else class="st-empty-note">
                                {{ featureEventTypeHint('stats_funnel') }}
                            </p>
                        </div>
                    </section>

                    <!-- Composition -->
                    <section class="st-card">
                        <div class="st-card__header">
                            <div class="st-card__icon st-card__icon--green">
                                <font-awesome-icon icon="fa-solid fa-people-group" />
                            </div>
                            <div>
                                <h2 class="st-card__title">Compozitie participanti</h2>
                                <p class="st-card__desc">{{ compositionSummary }}</p>
                            </div>
                        </div>
                        <div class="st-card__body">
                            <template v-if="hasFeature('stats_composition') || (!scopedProjectData.features?.length && !isSimpleTemplate)">
                                <div class="st-funnel" v-for="item in compositionItems" :key="item.key">
                                    <div class="st-funnel__top">
                                        <span>{{ item.label }}</span>
                                        <strong>{{ item.value }}</strong>
                                    </div>
                                    <div class="st-bar">
                                        <div class="st-bar__fill st-bar__fill--green" :style="{ width: item.width + '%' }"></div>
                                    </div>
                                </div>
                            </template>
                            <p v-else-if="isSimpleTemplate" class="st-empty-note">
                                Detaliile despre adulti/copii/cazare sunt disponibile in lista de raspunsuri de mai jos.
                            </p>
                            <p v-else class="st-empty-note">
                                {{ featureEventTypeHint('stats_composition') }}
                            </p>
                        </div>
                    </section>
                </div>

                <!-- ═══ Participants table ═══ -->
                <section class="st-card">
                    <div class="st-card__header">
                        <div class="st-card__icon st-card__icon--slate">
                            <font-awesome-icon icon="fa-solid fa-table-list" />
                        </div>
                        <div>
                            <h2 class="st-card__title">{{ isSimpleTemplate ? 'Raspunsuri formular' : 'Status invitati' }}</h2>
                            <p class="st-card__desc">
                                {{ confirmationsCount }} confirma &middot; {{ pendingResponsesCount }} in asteptare
                            </p>
                        </div>
                    </div>
                    <div class="st-card__body">
                        <GetParticipantsListStats
                            v-if="scopedProjectData && !isSimpleTemplate"
                            :projectId="currentProjectId"
                        />
                        <GetParticipantsListStatsSimple
                            v-if="scopedProjectData && isSimpleTemplate"
                            :projectId="currentProjectId"
                        />
                    </div>
                </section>
            </template>

            <!-- Loading -->
            <div v-else-if="loading" class="st-loading">
                <span class="loading loading-spinner loading-lg"></span>
            </div>
        </div>
    </div>
</template>

<script>
import GetParticipantsListStats from '@/components/GetParticipantsListStats.vue';
import GetParticipantsListStatsSimple from '@/components/GetParticipantsListStatsSimple.vue';
import UserTopNav from '@/components/UserTopNav.vue';
import { mapGetters } from 'vuex';
import { hasFeature } from '@/composables/useProjectFeatures';

export default {
    name: 'ProjectDetailsStats',
    props: {
        projectId: {},
        projectName: {
            type: String,
        },
        projectSlug: {
            type: String,
        },
    },
    components: {
        GetParticipantsListStats,
        GetParticipantsListStatsSimple,
        UserTopNav,
    },
    data() {
        return {
            currentProjectId: null,
        };
    },
    created() {
        this.currentProjectId = this.$route.params.projectId;
    },
    mounted() {
        if (
            !this.scopedProjectData ||
            this.scopedProjectData.projectId != this.$route.params.projectId
        ) {
            const projectId = this.$route.params.projectId;
            const options = {};
            this.$store.dispatch('fetchProjectUsers', {
                projectId,
                options,
                participants: true,
            });
        }
    },
    computed: {
        ...mapGetters([
            'projects',
            'plan',
            'loading',
            'scopedProjectData',
            'stats',
        ]),
        currentProjectName() {
            if (this.scopedProjectData && this.scopedProjectData.project_name) {
                return this.scopedProjectData.project_name;
            }
            return this.$route.params.projectSlug || '...';
        },
        currentProjectSlug() {
            if (this.scopedProjectData && this.scopedProjectData.project_slug) {
                return this.scopedProjectData.project_slug;
            }
            return this.$route.params.projectSlug || '';
        },
        isSimpleTemplate() {
            return this.scopedProjectData?.template_type === 'simple';
        },
        participantsList() {
            return this.scopedProjectData?.participants || [];
        },
        answeredCount() {
            return this.stats?.totalAnswers || 0;
        },
        confirmationsCount() {
            return this.stats?.totalConfirmations || 0;
        },
        refusalsCount() {
            return this.stats?.totalRefusals || 0;
        },
        totalInviteCount() {
            if (this.isSimpleTemplate) {
                return this.stats?.totalResponses || 0;
            }

            return this.participantsList.length;
        },
        totalViewsCount() {
            return this.stats?.totalViews || this.scopedProjectData?.public_views || 0;
        },
        pendingResponsesCount() {
            return Math.max(this.totalInviteCount - this.answeredCount, 0);
        },
        responseRate() {
            if (!this.totalInviteCount) {
                return 0;
            }

            return Math.min(
                100,
                Math.round((this.answeredCount / this.totalInviteCount) * 100)
            );
        },
        responseRateLabel() {
            return `${this.answeredCount}/${this.totalInviteCount} (${this.responseRate}%)`;
        },
        confirmationRate() {
            if (!this.totalInviteCount) {
                return 0;
            }

            return Math.round((this.confirmationsCount / this.totalInviteCount) * 100);
        },
        refusalRate() {
            if (!this.totalInviteCount) {
                return 0;
            }

            return Math.round((this.refusalsCount / this.totalInviteCount) * 100);
        },
        pendingRate() {
            if (!this.totalInviteCount) {
                return 0;
            }

            return Math.round((this.pendingResponsesCount / this.totalInviteCount) * 100);
        },
        templateTypeLabel() {
            return this.isSimpleTemplate ? 'Template simplu' : 'Template complex';
        },
        sentInvitesCount() {
            if (this.isSimpleTemplate) {
                return this.totalInviteCount;
            }

            return this.participantsList.filter(
                (entry) => entry.email_sent_time || entry.sms_sent_time || entry.whatsapp_shared_time
            ).length;
        },
        openedInvitesCount() {
            if (this.isSimpleTemplate) {
                return this.totalViewsCount;
            }

            return this.participantsList.filter((entry) => entry.opened_time).length;
        },
        funnelSteps() {
            const base = this.totalInviteCount || 1;
            return [
                {
                    key: 'sent',
                    label: 'Trimise',
                    value: this.sentInvitesCount,
                    rate: Math.round((this.sentInvitesCount / base) * 100),
                },
                {
                    key: 'opened',
                    label: 'Deschise',
                    value: this.openedInvitesCount,
                    rate: Math.round((this.openedInvitesCount / base) * 100),
                },
                {
                    key: 'answered',
                    label: 'Cu raspuns',
                    value: this.answeredCount,
                    rate: Math.round((this.answeredCount / base) * 100),
                },
            ];
        },
        deliverySummary() {
            if (this.isSimpleTemplate) {
                return 'Raspunsuri primite in formularul simplu';
            }

            return `${this.sentInvitesCount} trimise · ${this.openedInvitesCount} deschise`;
        },
        compositionData() {
            if (this.isSimpleTemplate) {
                return {
                    adults: 0,
                    children: 0,
                    accommodation: 0,
                    vegetarian: 0,
                };
            }

            return this.participantsList.reduce(
                (acc, entry) => {
                    if (entry.attendance_status === 'Attending') {
                        acc.adults += Number(entry.response_adults || 0);
                        acc.children += Number(entry.response_children || 0);
                        acc.accommodation += entry.response_accommodation ? 1 : 0;
                        acc.vegetarian += entry.response_menu_type === 'vegetarian' ? 1 : 0;
                    }

                    return acc;
                },
                {
                    adults: 0,
                    children: 0,
                    accommodation: 0,
                    vegetarian: 0,
                }
            );
        },
        compositionMax() {
            return Math.max(
                this.compositionData.adults,
                this.compositionData.children,
                this.compositionData.accommodation,
                this.compositionData.vegetarian,
                1
            );
        },
        compositionItems() {
            return [
                {
                    key: 'adults',
                    label: 'Adulti',
                    value: this.compositionData.adults,
                },
                {
                    key: 'children',
                    label: 'Copii',
                    value: this.compositionData.children,
                },
                {
                    key: 'accommodation',
                    label: 'Solicitari cazare',
                    value: this.compositionData.accommodation,
                },
                {
                    key: 'vegetarian',
                    label: 'Meniu vegetarian',
                    value: this.compositionData.vegetarian,
                },
            ].map((item) => ({
                ...item,
                width: item.value ? Math.max(8, Math.round((item.value / this.compositionMax) * 100)) : 0,
            }));
        },
        compositionSummary() {
            if (this.isSimpleTemplate) {
                return `${this.answeredCount} raspunsuri inregistrate`;
            }

            return `${this.compositionData.adults + this.compositionData.children} persoane estimate`;
        },
        viewsPerInviteLabel() {
            if (!this.totalInviteCount) {
                return 'Fara trafic inregistrat';
            }

            return `${(this.totalViewsCount / this.totalInviteCount).toFixed(1)} vizualizari / invitat`;
        },
        engagementScore() {
            if (!this.totalInviteCount) {
                return 0;
            }

            const openRate = this.isSimpleTemplate
                ? this.responseRate
                : Math.round((this.openedInvitesCount / this.totalInviteCount) * 100);
            const score = Math.round(this.responseRate * 0.75 + openRate * 0.25);
            return Math.min(score, 100);
        },
    },
    methods: {
        hasFeature(featureSlug) {
            return hasFeature(this.scopedProjectData, featureSlug);
        },
        formatDate(dateString) {
            if (!dateString) {
                return '-';
            }

            const months = [
                'Ian',
                'Feb',
                'Mar',
                'Apr',
                'Mai',
                'Iun',
                'Iul',
                'Aug',
                'Sep',
                'Oct',
                'Noi',
                'Dec',
            ];

            const [, monthIndex, day] = dateString.split('-');
            const month = months[parseInt(monthIndex, 10) - 1];

            return `${parseInt(day, 10)} ${month}`;
        },
        featureEventTypeHint(featureSlug) {
            const map = this.scopedProjectData?.feature_event_types || {};
            const labels = map[featureSlug] || [];
            if (!labels.length) return 'Indisponibil pentru tipul de eveniment curent.';
            return `Disponibil pentru evenimentele de tip ${labels.join(', ')}.`;
        },
        formatDateLarge(dateString) {
            if (!dateString) {
                return '-';
            }

            const months = [
                'Ian',
                'Feb',
                'Mar',
                'Apr',
                'Mai',
                'Iun',
                'Iul',
                'Aug',
                'Sep',
                'Oct',
                'Noi',
                'Dec',
            ];

            const [year, monthIndex, day] = dateString.split('-');
            const month = months[parseInt(monthIndex, 10) - 1] || '';
            return `${parseInt(day, 10)} ${month} ${year}`;
        },
    },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════
   Layout — matches ProjectDetails / Config
   ═══════════════════════════════════════════════ */
.st { min-height: 100vh; background: #f8fafc; }

.st-container {
    max-width: 1280px;
    width: 100%;
    margin: 0 auto;
    padding: 0 20px 60px;
    text-align: left;
}

/* ── Back link ── */
.st-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    text-decoration: none;
    padding: 24px 0 16px;
    transition: color 0.15s;
}
.st-back:hover { color: #0f172a; }

/* ── Header ── */
.st-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 28px;
}
.st-header__actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
    padding-top: 4px;
}
.st-header__name {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 2px;
    line-height: 1.2;
}
.st-header__sub {
    font-size: 14px;
    color: #64748b;
    margin: 0 0 12px;
}
.st-header__meta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.st-tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    background: #f1f5f9;
    padding: 4px 10px;
    border-radius: 8px;
}
.st-tag--type {
    background: #ede9fe;
    color: #7c3aed;
}
.st-tag--event {
    background: #fef3c7;
    color: #b45309;
}
.st-tag--engagement {
    background: #dbeafe;
    color: #1d4ed8;
}

/* ── Buttons ── */
.st-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 20px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 700;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.15s;
    white-space: nowrap;
    background: #0f172a;
    color: #fff;
}
.st-btn:hover {
    background: #1e293b;
    transform: translateY(-1px);
    color: #fff;
}
.st-btn--ghost {
    background: #f1f5f9;
    color: #475569;
}
.st-btn--ghost:hover {
    background: #e2e8f0;
    color: #0f172a;
}

/* ═══════════════════════════════════════════════
   KPI grid
   ═══════════════════════════════════════════════ */
.st-kpi-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    margin-bottom: 20px;
}
.st-kpi {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 20px;
    background: #fff;
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    transition: all 0.18s;
}
.st-kpi:hover {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    transform: translateY(-1px);
}
.st-kpi__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    flex-shrink: 0;
}
.st-kpi__icon--blue   { background: #dbeafe; color: #2563eb; }
.st-kpi__icon--violet { background: #ede9fe; color: #7c3aed; }
.st-kpi__icon--green  { background: #dcfce7; color: #16a34a; }
.st-kpi__icon--red    { background: #fee2e2; color: #ef4444; }
.st-kpi__icon--amber  { background: #fef3c7; color: #d97706; }
.st-kpi__icon--cyan   { background: #cffafe; color: #0891b2; }

.st-kpi__body {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
.st-kpi__label {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}
.st-kpi__value {
    font-size: 24px;
    font-weight: 800;
    color: #0f172a;
    line-height: 1.2;
}
.st-kpi__hint {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 1px;
}

/* ═══════════════════════════════════════════════
   Card (shared pattern from ProjectDetails/Config)
   ═══════════════════════════════════════════════ */
.st-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.04);
    margin-bottom: 16px;
}
.st-card__header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 24px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;
}
.st-card__icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}
.st-card__icon--blue   { background: #dbeafe; color: #2563eb; }
.st-card__icon--violet { background: #ede9fe; color: #7c3aed; }
.st-card__icon--green  { background: #dcfce7; color: #16a34a; }
.st-card__icon--slate  { background: #f1f5f9; color: #475569; }
.st-card__title {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 2px;
}
.st-card__desc {
    font-size: 13px;
    color: #64748b;
    margin: 0;
    line-height: 1.4;
}
.st-card__body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

/* ═══════════════════════════════════════════════
   Progress bar (response rate)
   ═══════════════════════════════════════════════ */
.st-progress__track {
    display: flex;
    width: 100%;
    height: 10px;
    border-radius: 999px;
    background: #f1f5f9;
    overflow: hidden;
}
.st-progress__fill {
    height: 100%;
    transition: width 0.6s ease;
}
.st-progress__fill--ok { background: #22c55e; border-radius: 999px 0 0 999px; }
.st-progress__fill--no { background: #f43f5e; }

.st-split-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}
.st-split {
    font-size: 12px;
    font-weight: 700;
    border-radius: 20px;
    padding: 3px 10px;
}
.st-split--ok      { color: #166534; background: #dcfce7; }
.st-split--no      { color: #9f1239; background: #ffe4e6; }
.st-split--pending { color: #92400e; background: #fef3c7; }

/* ═══════════════════════════════════════════════
   Insights row (funnel + composition side-by-side)
   ═══════════════════════════════════════════════ */
.st-insights-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 16px;
}
.st-insights-row > .st-card {
    margin-bottom: 0;
}

/* Funnel / bar rows */
.st-funnel + .st-funnel { margin-top: 12px; }
.st-funnel__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    color: #334155;
    margin-bottom: 4px;
}
.st-funnel__top strong { color: #0f172a; }
.st-funnel__pct {
    display: block;
    margin-top: 3px;
    font-size: 11px;
    color: #94a3b8;
}

.st-bar {
    width: 100%;
    height: 8px;
    border-radius: 999px;
    background: #f1f5f9;
    overflow: hidden;
}
.st-bar__fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.5s ease;
}
.st-bar__fill--blue  { background: linear-gradient(90deg, #3b82f6, #06b6d4); }
.st-bar__fill--green { background: linear-gradient(90deg, #22c55e, #14b8a6); }

.st-empty-note {
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
    margin: 0;
}

/* ═══════════════════════════════════════════════
   Loading
   ═══════════════════════════════════════════════ */
.st-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
}

/* ═══════════════════════════════════════════════
   Mobile
   ═══════════════════════════════════════════════ */
@media (max-width: 768px) {
    .st-container { padding: 0 16px 40px; }
    .st-header { flex-direction: column; gap: 12px; }
    .st-header__actions { width: 100%; }
    .st-btn { padding: 8px 14px; font-size: 12px; }
    .st-header__name { font-size: 22px; }
    .st-kpi-grid { grid-template-columns: 1fr 1fr; gap: 10px; }
    .st-kpi { padding: 14px 16px; gap: 12px; }
    .st-kpi__icon { width: 38px; height: 38px; font-size: 15px; }
    .st-kpi__value { font-size: 20px; }
    .st-card { padding: 20px 18px; border-radius: 14px; }
    .st-card__header { gap: 12px; margin-bottom: 20px; padding-bottom: 16px; }
    .st-card__icon { width: 38px; height: 38px; font-size: 16px; }
    .st-insights-row { grid-template-columns: 1fr; gap: 12px; }
}

@media (max-width: 480px) {
    .st-kpi-grid { grid-template-columns: 1fr; }
    .st-kpi__value { font-size: 22px; }
}
</style>
